import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress, Button } from '@mui/material';
import { Line } from 'react-chartjs-2';
import { Chart, registerables } from 'chart.js';
import apiService from '../components/ReasuableFuncations/api';
import pptxgen from "pptxgenjs";

Chart.register(...registerables);

const BudgetAnalysis = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [budgets, setBudgets] = useState([]);
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: []
  });

  useEffect(() => {
    fetchBudgets();
  }, []);

  useEffect(() => {
    // Scroll to the top of the page when the component mounts
    window.scrollTo(0, 0);
  }, []);

  const fetchBudgets = async () => {
    setLoading(true);
    setError(null);
    const userData = JSON.parse(localStorage.getItem('user'));
        if (!userData) {
        console.error('User data not found. Redirecting to login.');
        return;
      }
    const { selectedOption, regionalNetworkOption, memberStateOption, countryOption } = userData;
 
    let url = '/api/budgets';

    try {
      let response;

      if (selectedOption === 'Comesa') {
        response = await apiService.get(`${url}`);
      } else if (selectedOption === 'Regional Network' && regionalNetworkOption) {
        response = await apiService.get(`${url}?regionalNetwork=${regionalNetworkOption}`);
      } else if (selectedOption === 'Member State' && memberStateOption) {
        response = await apiService.get(`${url}?country=${countryOption}`);
      }

      if (!response.statusText === "OK") {
        throw new Error('Failed to fetch budgets');
      }

      const fetchedBudgets = response.data;
      setBudgets(fetchedBudgets);
      updateChartData(fetchedBudgets);
    } catch (error) {
      console.error('Error fetching budgets:', error);
      setError('Failed to fetch budgets. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const updateChartData = (fetchedBudgets) => {
    const sortedBudgets = fetchedBudgets.sort((a, b) => new Date(a.budgetDate) - new Date(b.budgetDate));

    const labels = sortedBudgets.map(budget => new Date(budget.budgetDate).toLocaleDateString());
    const expectedBudgets = sortedBudgets.map(budget => budget.expectedBudget);
    const actualBudgets = sortedBudgets.map(budget => budget.actualBudget);

    setChartData({
      labels: labels,
      datasets: [
        {
          label: 'Expected Budget',
          data: expectedBudgets,
          borderColor: 'rgba(75, 192, 192, 1)',
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
          fill: true,
        },
        {
          label: 'Actual Budget',
          data: actualBudgets,
          borderColor: 'rgba(255, 99, 132, 1)',
          backgroundColor: 'rgba(255, 99, 132, 0.2)',
          fill: true,
        }
      ]
    });
  };

  const exportToPPT = () => {
    const pptx = new pptxgen();
    const slide = pptx.addSlide();
    slide.addText('Expected vs Actual Budget Over Time', { x: 0.5, y: 0.5, fontSize: 24 });
  
    // Add chart data to the slide
    slide.addChart(pptx.ChartType.line, [
      {
        name: 'Expected Budget',
        labels: chartData.labels,
        values: chartData.datasets[0].data,
      },
      {
        name: 'Actual Budget',
        labels: chartData.labels,
        values: chartData.datasets[1].data,
      },
    ], { x: 0.5, y: 1.5, w: 8, h: 4 });
  
    // Add detailed budget data to the slide
    slide.addTable([
      ['Date', 'Project', 'Expected Budget', 'Actual Budget'],
      ...budgets.map((budget) => [
        new Date(budget.budgetDate).toLocaleDateString(),
        budget.project.projectName,
        `$${budget.expectedBudget.toLocaleString()}`,
        `$${budget.actualBudget.toLocaleString()}`,
      ]),
    ], { x: 0.5, y: 6, w: 8, h: 2 });
  
    pptx.writeFile({ fileName: 'BudgetAnalysis.pptx' });
  };

  return (
    <Box sx={{height:'100%', width: '100%', backgroundColor: 'white', p: 2 }}>
      <Typography variant="h4" component="h1" gutterBottom align="center">
        Budget Analysis
      </Typography>

      <Button variant="contained" color="primary" onClick={exportToPPT} style={{ marginBottom: '20px' }}>
        Export to PPT
      </Button>

      <Box sx={{ height: '65vh', display: 'flex', justifyContent: 'center', alignItems: 'center', paddingRight:'24px' }}>
        {loading ? (
          <CircularProgress />
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          <Box sx={{ width: '100%', height: '100%' }}>
            <Line
              data={chartData}
              options={{
                responsive: true,
                maintainAspectRatio: false, 
                plugins: {
                  legend: {
                    position: 'top',
                  },
                  title: {
                    display: true,
                    text: 'Expected vs Actual Budget Over Time',
                  },
                  tooltip: {
                    callbacks: {
                      title: (context) => {
                        const index = context[0].dataIndex;
                        return `Date: ${chartData.labels[index]}\nProject: ${budgets[index].project.projectName}`;
                      },
                      label: (context) => {
                        const value = context.raw;
                        return `${context.dataset.label}: $${value.toLocaleString()}`;
                      }
                    }
                  }
                },
                scales: {
                  x: {
                    title: {
                      display: true,
                      text: 'Date'
                    }
                  },
                  y: {
                    beginAtZero: true,
                    title: {
                      display: true,
                      text: 'Budget Amount ($)'
                    },
                    ticks: {
                      callback: (value) => '$' + value.toLocaleString()
                    }
                  }
                }
              }}
              style={{ height: '100%', width: '100%' }} 
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default BudgetAnalysis;
